<script setup lang="ts">
// #719 観点B: モバイルナビ背景に敷く魔法研究会のマップ画像。
import { mdiStarOutline } from '@mdi/js'
import type { ComponentPublicInstance } from 'vue'
import labMapBg from '@/assets/images/map/AreaMap_ArcaneLab.webp'

import type { WorksSortKey } from '~/composables/api/useWorkApi'
import type { WorkGalleryTab } from '~/composables/useWorkGallery'

// #724: 「お気に入り」タブを追加。/arcanelab は authenticated ミドルウェアで
// 保護済みのため、タブ自体は常にサインイン文脈内で表示される。
const categories: WorkGalleryTab[] = [
  { key: 'new', label: '新着' },
  { key: 'featured', label: '注目' },
  { key: 'popular', label: '人気' },
  { key: 'favorites', label: 'お気に入り' },
]

// #803 追補: 12 は 5 列グリッドと噛み合わず（最終行が 5+5+2 で欠ける）、**最大画面の
// 1 画面すら埋まらない**ため、開いた直後にセンチネルへ到達して次ページを取ってしまう。
// 実測（2026-08-11・`.arcane-grid` の列数と `.arcane-list` の可視高から算出）:
//   1280x720=3列 / 1920x1080=4列 / 2560x1440・3840x2160=5列（上限）
//   最大画面では完全に見える行が 2 行 + 3 行目が部分的に見える = **画面上に 15 枚**
// 列数の上限 5 の倍数にして最終行の欠けを無くし、1 画面を埋める。
const PER_PAGE = 15

const runtimeConfig = useRuntimeConfig()

useSeoMeta({
  title: '魔法研究会 | Code & Magic',
  description: '自分で魔法を作る研究をしています。',
  ogTitle: '魔法研究会 | Code & Magic',
  ogDescription: '自分で魔法を作る研究をしています。',
  ogImage: `${runtimeConfig.public.serviceDomain}/images/ogPlayMakeShare_wLogo.webp`,
  twitterCard: 'summary_large_image',
})

// JSON-LD: CollectionPage (作品一覧)
useJsonLd({
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  'name': '魔法研究会 | Code & Magic',
  'description': 'Code & Magic ユーザーが作成したプログラミング作品の一覧。注目・人気・新着の作品をプレイできます。',
  'isPartOf': {
    '@type': 'WebApplication',
    'name': 'Code & Magic',
    'url': runtimeConfig.public.serviceDomain,
  },
})

definePageMeta({
  middleware: ['authenticated', 'confirm-game-state'],
  layout: false,
  usePageReady: true,
})

const dialog = ref<boolean>(false)

const { listWorks, createWorkSession } = useApi()
const { trackWorkPlay } = useFunnelEvent()
// #443: モバイルは没入(fullscreen)で開き、デスクトップはモーダル既定＋没入トグルを許可する。
const { isMobile } = useIsMobile()

const route = useRoute()
const initialTab = (['featured', 'popular', 'new', 'favorites'] as const).includes(route.query.tab as WorksSortKey)
  ? (route.query.tab as WorksSortKey)
  : 'new'
const activeCategory = ref<WorksSortKey>(initialTab)

// #803: 取得とページングは WorkGallery（+ useWorkGallery）が持つ。ここは
// 「どう取るか」だけを渡す。タブ状態はページ側に残す —— デスクトップ / モバイルで
// SceneMap のスロットが切り替わり WorkGallery が別インスタンスになるため、
// 画面回転やリサイズでタブが巻き戻らないようにする。
// #802: 「注目」のシャッフルの種。**この画面を開いている間ずっと同じ値**を使うことで、
// 追記スクロールでページを跨いでも並びが保たれる（ページごとに振り直すと同じ作品が
// 2 枚並ぶ）。開き直せば新しい種になる＝次に来たときは違う顔ぶれ、というのが
// 「注目」の設計意図（セレンディピティ）。
const featuredSeed = Math.floor(Math.random() * 2 ** 31)

const fetchWorks = async (page: number, perPage: number, sort: WorksSortKey) => {
  const resp = await listWorks(page, perPage, sort, sort === 'featured' ? featuredSeed : undefined)
  return { works: resp.works, total: resp.total }
}

const onCategoryChange = (key: WorksSortKey) => {
  activeCategory.value = key
  const url = new URL(window.location.href)
  url.searchParams.set('tab', key)
  window.history.replaceState(null, '', url.toString())
}

// #803: タブ切替時にデスクトップの内側スクロールを先頭へ戻す（`.arcane-list` が
// `overflow-y: auto` の器）。ここを戻さないと、積み増したぶんスクロールした状態で
// 別タブの短いリストに切り替わり、空白だけが見える。
// `v-container` は component なので、素の要素は `$el` から取る。
const listRef = ref<ComponentPublicInstance | null>(null)
const listEl = computed<HTMLElement | null>(
  () => (listRef.value?.$el as HTMLElement | undefined) ?? null,
)

const { finishLoading } = usePageReady()

const onClickRow = (): void => {
  dialog.value = true
}

// 作品の説明は HTML を含みうるので、モバイルカードの2行プレビューではタグを除去。
const plainText = (s: string): string =>
  (s || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()

const sessionUrl = ref<string>('')
// #663: Pyodide built-in packages from the session response.
const sessionPackages = ref<string[]>([])
const currentWork = ref<Work | null>(null)

const onPlayCode = async (work: Work): Promise<void> => {
  const session = await createWorkSession(work.workId)
  trackWorkPlay(work.publicUrl.split('/').pop() ?? '', false)
  sessionUrl.value = session.sessionUrl
  sessionPackages.value = session.pyodidePackages ?? []
  currentWork.value = work
  dialog.value = true
}
// #722: 再生中の BGM 停止は PlayDialog 自身が担う（ページ側では制御しない）。
</script>

<template>
  <NuxtLayout :name="isMobile ? 'mobile-nav' : 'centering43'">
    <SceneMap
      scene-id="cmp_arcaneLab"
      back-to-text="冒険者のキャンプに戻る"
      :mobile="isMobile"
      :mobile-background="labMapBg"
      @click-scene="onClickRow"
    >
      <template #map>
        <v-img
          src="@/assets/images/map/AreaMap_ArcaneLab.webp"
          width="100%"
          height="auto"
          lazy-src="@/assets/images/map/LazyAreaMap_ArcaneLab.png"
          :transition="false"
        />

        <v-container
          ref="listRef"
          fluid
          class="arcane-list"
        >
          <!-- #803: タブ・取得・追記スクロールは WorkGallery が持つ。ここは
               並べ方（`.arcane-grid`）とカードだけを渡す。 -->
          <WorkGallery
            :tabs="categories"
            :sort="activeCategory"
            :fetcher="fetchWorks"
            :per-page="PER_PAGE"
            :scroll-root="listEl"
            end-text="すべての作品を見ました"
            @update:sort="onCategoryChange"
            @loaded="finishLoading"
          >
            <template #empty>
              <div
                v-if="activeCategory === 'favorites'"
                class="arcane-empty"
              >
                <v-icon
                  :icon="mdiStarOutline"
                  size="40"
                />
                <p class="arcane-empty-title">
                  まだお気に入りがありません
                </p>
                <p class="arcane-empty-sub">
                  気になった作品の ★ を押すと、ここに集まります。
                </p>
              </div>
            </template>

            <template #default="{ works }">
              <div class="arcane-grid">
                <WorkCard
                  v-for="work in works"
                  :key="work.workId"
                  :work="work"
                  @play="onPlayCode(work)"
                />
              </div>
            </template>
          </WorkGallery>
        </v-container>

        <DialogPlayDialog
          v-if="dialog"
          v-model="dialog"
          :session-url="sessionUrl"
          :share-slug="currentWork?.publicUrl?.split('/').pop()"
          :share-title="currentWork?.title"
          :pyodide-packages="sessionPackages"
          :gamepad-compatible="currentWork?.gamepadCompatible ?? false"
          :show-gamepad-emulator="currentWork?.gamepadCompatible ?? false"
          :allow-fullscreen="!isMobile"
          :fixed-fullscreen="isMobile"
          back-text="リストに戻る"
          @back="dialog = false"
        />
      </template>

      <!-- #719 観点B: モバイルは作品ギャラリー（タブ＋作品リスト）。プレイは
         #443 の PlayDialog を没入(fullscreen)で開く。 -->
      <template #mobile>
        <div class="arcane-mobile">
          <!-- #803: モバイルはウィンドウスクロール。器は同じ WorkGallery で、
               カードだけ横型の MobileListCard に差し替える（#719 の使い分けを維持）。 -->
          <WorkGallery
            :tabs="categories"
            :sort="activeCategory"
            :fetcher="fetchWorks"
            :per-page="PER_PAGE"
            tab-variant="mobile"
            end-text="すべての作品を見ました"
            @update:sort="onCategoryChange"
            @loaded="finishLoading"
          >
            <template #empty>
              <div
                v-if="activeCategory === 'favorites'"
                class="arcane-empty arcane-empty--mobile"
              >
                <v-icon
                  :icon="mdiStarOutline"
                  size="36"
                />
                <p class="arcane-empty-title">
                  まだお気に入りがありません
                </p>
                <p class="arcane-empty-sub">
                  気になった作品の ★ を押すと、ここに集まります。
                </p>
              </div>
            </template>

            <template #default="{ works }">
              <div class="arcane-mobile-list">
                <MobileListCard
                  v-for="work in works"
                  :key="work.workId"
                  :image="work.coverImageUrl"
                  :image-webp="work.coverWebpUrl"
                  :title="work.title"
                  :subtitle="plainText(work.description)"
                  @click="onPlayCode(work)"
                >
                  <!-- #724/#752: お気に入り★はサムネ右上へ（カード全体は tap-to-play
                       なので WorkFavoriteButton 内の @click.stop で誤爆を防ぐ）。 -->
                  <template #imageOverlay>
                    <div class="mobile-favorite">
                      <WorkFavoriteButton :work="work" />
                    </div>
                  </template>
                  <template #meta>
                    <div class="arcane-tags">
                      <span
                        v-for="k in (work.keywords ?? []).slice(0, 3)"
                        :key="k"
                        class="mobile-tag"
                      >{{ k }}</span>
                    </div>
                    <div class="mobile-meta-row">
                      <div class="mobile-author">
                        by {{ work.author }}
                      </div>
                    </div>
                  </template>
                </MobileListCard>
              </div>
            </template>
          </WorkGallery>
        </div>

        <DialogPlayDialog
          v-if="dialog"
          v-model="dialog"
          :session-url="sessionUrl"
          :share-slug="currentWork?.publicUrl?.split('/').pop()"
          :share-title="currentWork?.title"
          :pyodide-packages="sessionPackages"
          :gamepad-compatible="currentWork?.gamepadCompatible ?? false"
          :show-gamepad-emulator="currentWork?.gamepadCompatible ?? false"
          :allow-fullscreen="!isMobile"
          :fixed-fullscreen="isMobile"
          back-text="リストに戻る"
          @back="dialog = false"
        />
      </template>
    </SceneMap>
  </NuxtLayout>
</template>

<style scoped>
/* #719 観点B: モバイルの作品ギャラリー（mobile-nav レイアウト内・dvw/dvh 可）。 */
.arcane-mobile {
  padding: 4px 10px 16px;
  font-family: "Noto Sans JP", sans-serif;
}

/* 横型カード（MobileListCard）の縦リスト。 */
.arcane-mobile-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 8px 0;
  max-width: 560px;
  margin: 0 auto;
}

.mobile-tag {
  font-size: 0.72rem;
  color: #4a3a2a;
  background: rgba(0, 0, 0, 0.07);
  border-radius: 6px;
  padding: 1px 7px;
  white-space: nowrap;
}

/* タグは1行（折り返し可）、その下に作者行を独立させる。 */
.arcane-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  width: 100%;
}

/* 作者名 + お気に入り★を1行に並べる（#724）。 */
.mobile-meta-row {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.mobile-author {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 0.76rem;
  color: #60a0b0;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* #752: モバイルグリッドの★はサムネ右上に移設。背景は敷かず★単体で見せ、
   非お気に入り時は白（currentColor）＋ drop-shadow で任意サムネ上でも視認可。 */
.mobile-favorite {
  display: flex;
  line-height: 0;
  color: #fff;
  font-size: 20px;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.7));
}

/* お気に入りタブが空のときの誘導（#724）。 */
.arcane-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 40px 16px;
  color: #fff;
  text-align: center;
}

.arcane-empty--mobile {
  color: #4a3a2a;
  padding: 32px 16px;
}

.arcane-empty-title {
  font-size: 1.05rem;
  font-weight: 700;
  margin: 4px 0 0;
}

.arcane-empty-sub {
  font-size: 0.85rem;
  opacity: 0.85;
  margin: 0;
}

.arcane-list {
  font-family: "Noto Sans JP", sans-serif;
  position: absolute;

  top: 50%;
  left: 50%;

  translate: -50% -50%;
  width: 95%;
  height: 80%;

  color: white;
  background-color: rgb(128, 64, 64, 0.5);

  overflow-y: auto;
  scrollbar-color: #81D4FA #ffffff00;
  scrollbar-width: thin;
}

/* #670 Phase 1: WorkCard grid を auto-fill minmax(280px, 1fr) で
   揃える。fhd では 4 列、tablet/laptop は 2-3 列まで自動で落ちる。
   Phase 3 (#670 後半): 2K 以上で 6 列に達すると、card 内部の
   `--u` ベース (= centering43 幅の 1%) フォント/パディングが card 幅
   に対して大きすぎてタイトルやタグが切れる。`auto-fill` のまま列数
   上限を 5 に抑える CSS トリック: `max(280px, 5 列均等割 fair-share)`
   を minmax 下限にすることで、widescreen では fair-share が 280px を
   超えて勝手に列幅が広がり 6 列目が入らなくなる。 */
.arcane-grid {
  display: grid;
  grid-template-columns: repeat(
    auto-fill,
    minmax(max(280px, calc((100% - 4 * 12px) / 5)), 1fr)
  );
  align-items: start;
  gap: 12px;
  padding: 4px;
}

/* #803: タブは WorkGalleryTabs に移設。centering43 の中なので、フォントサイズだけ
   `--u` ベースで渡す（landing 既定の dvw をここで使ってはいけない）。 */
.arcane-list :deep(.wg-tabs--pill) {
  --wg-tab-font-size: calc(1.8 * var(--u));

  padding-top: 8px;
}

/* 末尾ローダー（スピナー / 打ち止め文言）は暗い背景の上に載るので白に。 */
.arcane-list :deep(.wg-loader),
.arcane-mobile :deep(.wg-loader) {
  color: #fff;
}

.enter-button {
  position: absolute;
  left: 72.5%;
  top: 92.5%;

  font-size: max(13.89px, calc(1.95 * var(--u)));
  font-weight: bold;

  background-color: rgba(255, 255, 255, 0.6);
  z-index: 3;
}
</style>
